<template>
    <div class="container admin-table-page">
        <!-- Título y botón de retroceso -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ f_titulo() }}</h2>
                </div>
            </div>
        </div>

        <!-- Filtros principales -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <div class="filter-date-wrapper">
                        <input 
                            type="month" 
                            v-model="selVigencia" 
                            @change="f_validatePeriodo"
                            class="filter-date-input"
                            :class="{ 'disabled-period': isPeriodoDeshabilitado(selVigencia) }"
                            :disabled="isLoading || isLoadingCarga"
                        />
                    </div>

                    <b-form-select
                        v-model="tipoCarga"
                        @change="f_onTipoCargaChange"
                        class="filter-select ml-2"
                        :disabled="!selVigencia || isPeriodoDeshabilitado(selVigencia) || isLoadingCarga"
                    >
                        <b-form-select-option value="">{{ labels.seleccione }}</b-form-select-option>
                        <b-form-select-option value="ajustes">{{ labels.ajustes }}</b-form-select-option>
                        <b-form-select-option value="reclasificaciones">{{ labels.reclasificaciones }}</b-form-select-option>
                        <b-form-select-option value="topside">{{ labels.topside }}</b-form-select-option>
                    </b-form-select>
                </div>
            </div>
        </div>

        <!-- Alerta de período deshabilitado -->
        <b-alert 
            v-if="selVigencia && isPeriodoDeshabilitado(selVigencia)" 
            show 
            variant="danger" 
            class="mt-3 text-center"
        >
            <font-awesome-icon :icon="['fas', 'exclamation-octagon']" class="mr-2" />
            <strong>PERÍODO DESHABILITADO:</strong> No se pueden cargar datos para el período
            {{ formatFecha(selVigencia) }} porque ha sido deshabilitado por el administrador.
            <br>
            <small>Si necesita cargar información para este período, contacte al administrador.</small>
        </b-alert>

        <!-- Tarjeta de carga -->
        <div class="admin-table-block mt-4">
            <div v-if="!isLoading">
                <div class="table-corporate-shell">
                    <div v-if="isUploading" class="upload-spinner-container">
                        <div class="modern-spinner">
                            <div class="spinner-ring"></div>
                            <div class="spinner-ring"></div>
                            <div class="spinner-ring"></div>
                        </div>
                        <p class="mt-3">{{ labels.loading }}</p>
                    </div>

                    <div v-else class="upload-content-wrapper">
                        <!-- Área de drop -->
                        <div
                            class="upload-drop-zone"
                            :class="{ 
                                'drag-over': isDragOver,
                                'has-file': fileName,
                                'disabled': !isFormularioCompleto || isLoadingCarga || showSuccessMessage
                            }"
                            @dragover.prevent="isDragOver = true"
                            @dragleave.prevent="isDragOver = false"
                            @drop.prevent="handleDrop"
                            @click="triggerFileInput"
                        >
                            <input
                                ref="fileInput"
                                type="file"
                                accept=".xlsx,.xls"
                                class="hidden-file-input"
                                :disabled="isLoadingCarga || showSuccessMessage"
                                @change="cargaExcel"
                            />

                            <div class="upload-content">
                                <font-awesome-icon
                                    :icon="['fas', 'cloud-upload-alt']"
                                    class="upload-icon"
                                    :class="{ 'has-file': fileName }"
                                />

                                <h5 class="upload-title" :class="{ 'text-muted': !isFormularioCompleto || isLoadingCarga || showSuccessMessage }">
                                    {{ isLoadingCarga ? 'Procesando carga...' : (showSuccessMessage ? 'Carga completada' : (fileName || labels.dropOrClick)) }}
                                </h5>

                                <p v-if="!fileName && !isFormularioCompleto && !isLoadingCarga && !showSuccessMessage" class="upload-subtitle text-muted">
                                    <small>{{ labels.completeFilters }}</small>
                                </p>

                                <p v-if="!fileName && isFormularioCompleto && !isLoadingCarga && !showSuccessMessage" class="upload-subtitle">
                                    <small>{{ labels.supportedFormats }}</small>
                                </p>

                                <div v-if="fileName && !isLoadingCarga && !showSuccessMessage" class="upload-file-info-wrapper">
                                    <font-awesome-icon :icon="['fas', 'file-excel']" class="text-success mr-2" />
                                    <span>{{ fileName }}</span>
                                    <b-button size="sm" variant="link" class="remove-file-btn ml-2" @click.stop="removeFile">
                                        <font-awesome-icon :icon="['fas', 'times']" />
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <!-- Selector de hoja y botón de descarga en la misma fila -->
                        <div class="sheet-selector-container mt-3">
                            <div class="d-flex align-items-center justify-content-center" style="gap: 12px; flex-wrap: nowrap;">
                                <b-form-select
                                    v-model="selectedHoja"
                                    @change="f_showHoja($event)"
                                    class="filter-select sheet-select"
                                    :disabled="!isFormularioCompleto || isLoadingCarga || showSuccessMessage || listHojas.length === 0"
                                >
                                    <b-form-select-option value="">{{ labels.selectSheet }}</b-form-select-option>
                                    <option v-for="(item, idx) in listHojas" :key="idx" :value="item">{{ item }}</option>
                                </b-form-select>

                                <template v-if="showDownloadButton">
                                    <span class="novedades-label">
                                        <font-awesome-icon :icon="['fas', 'clipboard-list']" class="mr-1" />
                                        Novedades del cargue anterior
                                    </span>
                                    <b-button
                                        class="download-btn"
                                        @click="descargarExcelErrores"
                                        :disabled="isLoadingCarga || !tieneErroresParaDescargar"
                                        variant="success"
                                    >
                                        <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-2" />
                                        {{ labels.downloadReport }}
                                    </b-button>
                                </template>
                            </div>
                        </div>

                        <!-- Panel de confirmación -->
                        <div v-if="!isLoadingCarga && !showSuccessMessage" class="upload-action-panel mt-4">
                            <div class="confirmation-info-grid">
                                <div class="info-item">
                                    <span class="info-label">{{ labels.validity }}:</span>
                                    <span class="info-value" :class="{ 'text-muted': !selVigencia }">
                                        {{ selVigencia ? formatFecha(selVigencia) : 'No seleccionada' }}
                                    </span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">{{ labels.loadType }}:</span>
                                    <span class="info-value" :class="{ 'text-muted': !tipoCarga }">
                                        {{ tipoCarga ? getTipoLabel(tipoCarga) : 'No seleccionado' }}
                                    </span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">{{ labels.file }}:</span>
                                    <span class="info-value" :class="{ 'text-muted': !fileName }">
                                        {{ getFileName() || 'No seleccionado' }}
                                    </span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">{{ labels.sheet }}:</span>
                                    <span class="info-value" :class="{ 'text-muted': !selectedHoja }">
                                        {{ selectedHoja || 'No seleccionada' }}
                                    </span>
                                </div>
                                <div class="info-item" v-if="registrosCount > 0">
                                    <span class="info-label">{{ labels.records }}:</span>
                                    <span class="info-value">{{ registrosCount }} registros</span>
                                </div>
                            </div>

                            <div class="alert alert-warning text-center mt-3" role="alert">
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                {{ labels.confirmWarning }}
                            </div>

                            <div class="text-center mt-3">
                                <b-button
                                    class="upload-btn"
                                    :disabled="!confirmButtonEnabled || isPeriodoDeshabilitado(selVigencia) || !selectedHoja || validacionFallida"
                                    @click="confirmAndLoad()"
                                >
                                    <font-awesome-icon :icon="['fas', 'check']" class="mr-2" />
                                    {{ confirmButtonEnabled && selectedHoja ? labels.acceptAndLoad : `${labels.wait} ${countdown} ${labels.seconds}` }}
                                </b-button>
                            </div>
                        </div>

                        <!-- ALERTA GENERAL DE VALIDACIÓN -->
                        <div v-if="erroresValidacion.length > 0 && !isLoadingCarga" class="mt-3">
                            <b-alert show variant="danger">
                                <div class="d-flex align-items-center mb-2">
                                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                    <strong>
                                        VALIDACIÓN FALLIDA —
                                        {{ tipoCarga === 'reclasificaciones' ? 'RECLASIFICACIONES' : 'TOPSIDE' }}
                                    </strong>
                                </div>

                                <p class="mb-2">
                                    Se encontraron
                                    <b>{{ erroresValidacion.length }}</b>
                                    error(es). Corrija el archivo y vuelva a cargarlo.
                                    <b>No se cargará ningún dato.</b>
                                </p>

                                <div style="max-height: 250px; overflow-y: auto; background: #fff; border-radius: 6px; padding: 10px; border: 1px solid #f5c6cb;">
                                    <ul class="mb-0" style="padding-left: 20px;">
                                        <li v-for="(err, idx) in erroresValidacion" :key="idx" style="font-size: 0.9rem; margin-bottom: 4px;">
                                            <b v-if="err.cuenta">Cuenta {{ err.cuenta }}:</b>
                                            <b v-else-if="err.fila">Fila {{ err.fila }}:</b>
                                            {{ err.mensaje }}
                                        </li>
                                    </ul>
                                </div>

                                <div class="mt-3">
                                    <b-button @click="descargarExcelErroresValidacion" variant="primary" size="sm">
                                        <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                                        Descargar Excel con errores
                                    </b-button>
                                </div>
                            </b-alert>
                        </div>

                        <!-- Spinner de carga moderno con barra de progreso -->
                        <div v-if="isLoadingCarga" class="upload-spinner-container mt-4">
                            <div class="modern-spinner">
                                <div class="spinner-ring"></div>
                                <div class="spinner-ring"></div>
                                <div class="spinner-ring"></div>
                            </div>
                            <p class="mt-3 font-weight-bold">{{ progressMessage }}</p>
                            <p class="text-muted small">{{ progressSubMessage }}</p>
                            <b-progress class="mt-2 modern-progress" style="max-width: 400px; width: 100%; height: 8px; border-radius: 4px;">
                                <b-progress-bar 
                                    :value="progressValue" 
                                    :label="`${progressValue}%`" 
                                    variant="primary" 
                                    striped 
                                    animated 
                                    class="rounded-pill"
                                />
                            </b-progress>
                        </div>

                        <!-- Mensaje de éxito -->
                        <div v-if="showSuccessMessage" class="upload-spinner-container mt-4">
                            <div class="success-message">
                                <font-awesome-icon :icon="['fas', 'check-circle']" class="success-icon" />
                                <p class="success-title">¡Carga completada exitosamente!</p>
                                <p class="success-subtitle">El archivo ha sido procesado correctamente</p>
                            </div>
                        </div>

                        <!-- ALERTA DE CARGA PARCIAL -->
                        <div v-if="cargaParcial && !isLoadingCarga" class="mt-3">
                            <div class="alert alert-warning text-center" role="alert">
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                <strong>Carga parcial:</strong>
                                {{ registrosProcesadosCount }}/{{ totalRegistros }} OK
                                <span class="text-danger ml-2">{{ registrosConError }} errores</span>
                                <br>
                                <span v-if="mensajeError" class="text-muted" style="font-size: 0.9rem;">{{ mensajeError }}</span>
                            </div>
                        </div>

                        <!-- ALERTA DE ERROR DE CARGA -->
                        <div v-if="cargaFallida && !isLoadingCarga" class="mt-3">
                            <div class="alert alert-danger text-center" role="alert">
                                <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="mr-2" />
                                <strong>Carga fallida:</strong>
                                {{ registrosConError }} errores
                                <br>
                                <span v-if="mensajeError" class="text-muted" style="font-size: 0.9rem;">{{ mensajeError }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isLoading" class="text-center py-5">
                <div class="modern-spinner">
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                </div>
                <p class="mt-3">{{ labels.loading }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import Cargue from "../../services/Cargue"
import * as XLSX from "xlsx"

export default {
    name: "CargueAjustes",
    data: () => ({
        selVigencia: "",
        tipoCarga: "",
        listHojas: [],
        selectedHoja: "",
        workbook: null,
        fileReader: null,
        fileName: "",
        isDragOver: false,
        isLoading: false,
        isUploading: false,
        isLoadingCarga: false,
        showSuccessMessage: false,
        showBtnHoja: false,
        confirmButtonEnabled: false,
        countdown: 5,
        countdownInterval: null,
        progressValue: 0,
        progressMessage: "Iniciando carga...",
        progressSubMessage: "Procesando archivo Excel",
        registrosProcesados: [],
        registrosCount: 0,
        cargaFallida: false,
        cargaParcial: false,
        mensajeError: "",
        registrosProcesadosCount: 0,
        registrosConError: 0,
        totalRegistros: 0,
        erroresDetalle: [],
        showDownloadButton: false,
        successTimer: null,

        // Validación unificada (reclasificaciones + topside)
        erroresValidacion: [],
        validacionFallida: false,
    }),

    computed: {
        isFormularioCompleto() {
            return !!(this.selVigencia && this.tipoCarga && !this.isPeriodoDeshabilitado(this.selVigencia))
        },

        tieneErroresParaDescargar() {
            return this.erroresDetalle && this.erroresDetalle.length > 0
        },

        labels() {
            const isSpanish = this.$store.state._language === "es-ES"
            return {
                selectValidity: isSpanish ? "Seleccione Vigencia" : "Select Validity",
                selectLoadType: isSpanish ? "Tipo de Carga" : "Load Type",
                selectSheet: isSpanish ? "Seleccione Hoja" : "Select Sheet",
                downloadReport: isSpanish ? "Descargar Novedades" : "Download Report",
                dropOrClick: isSpanish ? "Arrastre o haga clic para seleccionar un archivo Excel" : "Drag or click to select an Excel file",
                supportedFormats: isSpanish ? "Formatos soportados: .xlsx, .xls" : "Supported formats: .xlsx, .xls",
                completeFilters: isSpanish ? "Complete los filtros para habilitar la carga" : "Complete filters to enable upload",
                validity: isSpanish ? "Vigencia" : "Validity",
                loadType: isSpanish ? "Tipo de Carga" : "Load Type",
                file: isSpanish ? "Archivo" : "File",
                sheet: isSpanish ? "Hoja" : "Sheet",
                records: isSpanish ? "Registros" : "Records",
                confirmWarning: isSpanish ? "Por favor verifique que la fecha y tipo de carga sean correctos. Esta acción no se puede deshacer." : "Please verify that the date and load type are correct. This action cannot be undone.",
                acceptAndLoad: isSpanish ? "Aceptar y Cargar" : "Accept and Load",
                wait: isSpanish ? "Esperar" : "Wait",
                seconds: isSpanish ? "segundos" : "seconds",
                loading: isSpanish ? "Cargando..." : "Loading...",
                seleccione: isSpanish ? "Seleccione..." : "Select...",
                ajustes: isSpanish ? "Ajustes" : "Adjustments",
                reclasificaciones: isSpanish ? "Reclasificaciones" : "Reclassifications",
                topside: isSpanish ? "TopSide" : "TopSide",
            }
        }
    },

    watch: {
        selVigencia(newVal) {
            if (newVal && this.isPeriodoDeshabilitado(newVal)) {
                this.resetForm(true)
                this.$bvToast?.toast(
                    `El período ${this.formatFecha(newVal)} está deshabilitado. No se permiten cargas.`,
                    { title: 'Período Bloqueado', variant: 'warning', solid: true, autoHideDelay: 4000 }
                )
            }
        }
    },

    methods: {
        goBack() { this.$router.back() },

        f_titulo() {
            const titulo = "Proceso de carga de ajustes, reclasificaciones y topside"
            const tituloEN = "ADJUSTMENTS, RECLASSIFICATIONS AND TOPSIDE UPLOAD PROCESS"
            return this.$store.state._language !== "es-ES" ? tituloEN : titulo
        },

        formatFecha(fecha) {
            if (!fecha) return 'No seleccionada'
            const [year, month] = fecha.split('-')
            const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
            return `${meses[parseInt(month) - 1]} ${year}`
        },

        formatVigenciaBackend(vigencia) {
            if (!vigencia) return ''
            return vigencia.replace('-', '')
        },

        isPeriodoDeshabilitado(periodo) {
            if (!periodo) return false
            return !this.$store.getters.isPeriodoEnabled(this.formatVigenciaBackend(periodo))
        },

        f_validatePeriodo() {
            if (this.selVigencia && this.isPeriodoDeshabilitado(this.selVigencia)) {
                this.$bvToast?.toast(
                    `No se puede trabajar con el período ${this.formatFecha(this.selVigencia)} porque está deshabilitado.`,
                    { title: 'Período Deshabilitado', variant: 'danger', solid: true, autoHideDelay: 5000 }
                )
                this.resetForm(true)
                return false
            }
            return true
        },

        f_onTipoCargaChange() {
            this.showBtnHoja = false
            this.selectedHoja = ""
            this.listHojas = []
            this.fileName = ""
            this.registrosProcesados = []
            this.registrosCount = 0
            this.cargaFallida = false
            this.cargaParcial = false
            this.mensajeError = ""
            this.erroresDetalle = []
            this.registrosProcesadosCount = 0
            this.registrosConError = 0
            this.totalRegistros = 0
            this.showDownloadButton = false
            this.erroresValidacion = []
            this.validacionFallida = false
            this.resetCountdown()
            if (this.$refs.fileInput) this.$refs.fileInput.value = ''
            this.workbook = null
            this.fileName = ""
        },

        triggerFileInput() {
            if (this.isLoadingCarga || this.showSuccessMessage) return
            if (!this.isFormularioCompleto) {
                this.$bvToast?.toast('Complete todos los filtros para habilitar la carga.', { title: 'Validación', variant: 'warning', solid: true })
                return
            }
            this.$refs.fileInput?.click()
        },

        handleDrop(event) {
            if (this.isLoadingCarga || this.showSuccessMessage) return
            if (!this.isFormularioCompleto) {
                this.$bvToast?.toast('Complete todos los filtros para habilitar la carga.', { title: 'Validación', variant: 'warning', solid: true })
                return
            }
            this.isDragOver = false
            const files = event.dataTransfer.files
            if (files.length > 0) this.processFile(files[0])
        },

        processFile(file) {
            const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']
            if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/)) {
                this.$bvToast?.toast('Por favor seleccione un archivo Excel válido (.xlsx o .xls)', { title: 'Error', variant: 'danger', solid: true })
                return
            }

            this.isUploading = true
            this.fileName = file.name
            this.cargaFallida = false
            this.cargaParcial = false
            this.mensajeError = ""
            this.erroresDetalle = []
            this.registrosProcesadosCount = 0
            this.registrosConError = 0
            this.totalRegistros = 0
            this.showDownloadButton = false
            this.erroresValidacion = []
            this.validacionFallida = false

            this.fileReader = new FileReader()
            this.fileReader.onload = (event) => {
                try {
                    const data = event.target.result
                    this.workbook = XLSX.read(data, { type: "binary" })
                    this.listHojas = this.workbook.SheetNames
                    this.selectedHoja = ''
                    this.showBtnHoja = false
                    this.isUploading = false
                    this.registrosProcesados = []
                    this.registrosCount = 0
                    this.resetCountdown()
                    this.$bvToast?.toast(`Archivo "${file.name}" cargado correctamente`, { title: 'Éxito', variant: 'success', solid: true })
                } catch (error) {
                    this.isUploading = false
                    this.$bvToast?.toast('Error al leer el archivo Excel', { title: 'Error', variant: 'danger', solid: true })
                    console.error(error)
                }
            }
            this.fileReader.readAsBinaryString(file)
        },

        cargaExcel(event) {
            const file = event.target.files[0]
            if (file) this.processFile(file)
            event.target.value = ''
        },

        removeFile() {
            if (this.isLoadingCarga || this.showSuccessMessage) return
            this.fileName = ''
            this.selectedHoja = ''
            this.listHojas = []
            this.showBtnHoja = false
            this.workbook = null
            this.fileReader = null
            this.isUploading = false
            this.registrosProcesados = []
            this.registrosCount = 0
            this.cargaFallida = false
            this.cargaParcial = false
            this.mensajeError = ""
            this.erroresDetalle = []
            this.showDownloadButton = false
            this.erroresValidacion = []
            this.validacionFallida = false
            this.resetCountdown()
            if (this.$refs.fileInput) this.$refs.fileInput.value = ''
        },

        f_showHoja(hojaSeleccionada) {
            const valorHoja = hojaSeleccionada || this.selectedHoja
            if (this.isLoadingCarga || this.showSuccessMessage) return
            if (!valorHoja) return
            this.procesarDatosHoja(valorHoja)
            this.showBtnHoja = true
            this.startCountdown()
        },

        /**
         * Convierte un valor a número limpiando separadores de miles.
         * Acepta: "1234.56", "1,234.56", "-5.567.785.200", "5,567,785,200"
         * Devuelve NaN si no es numérico válido.
         */
        parseValorNumerico(valor) {
            if (valor === null || valor === undefined || valor === "") {
                return NaN
            }
            if (typeof valor === "number") {
                return valor
            }
            let str = String(valor).trim()
            str = str.replace(/\s+/g, "")

            const tieneComa = str.includes(",")
            const tienePunto = str.includes(".")

            if (tieneComa && tienePunto) {
                const posComa = str.lastIndexOf(",")
                const posPunto = str.lastIndexOf(".")
                if (posComa > posPunto) {
                    str = str.replace(/\./g, "").replace(",", ".")
                } else {
                    str = str.replace(/,/g, "")
                }
            } else if (tieneComa && !tienePunto) {
                str = str.replace(",", ".")
            }

            const num = parseFloat(str)
            return isNaN(num) ? NaN : num
        },

        /**
         * Valida que un string no esté vacío.
         */
        esVacio(val) {
            return val === null || val === undefined || String(val).trim() === ""
        },

        procesarDatosHoja(nombreHoja) {
            const hojaAProcesar = nombreHoja || this.selectedHoja
            try {
                if (!this.workbook || !hojaAProcesar) {
                    this.registrosCount = 0
                    this.registrosProcesados = []
                    return
                }

                const worksheet = this.workbook.Sheets[hojaAProcesar]
                if (!worksheet) {
                    this.registrosCount = 0
                    this.registrosProcesados = []
                    return
                }
                
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
                if (!jsonData || jsonData.length === 0) {
                    this.registrosCount = 0
                    this.registrosProcesados = []
                    this.$bvToast?.toast('La hoja seleccionada está vacía', { title: 'Advertencia', variant: 'warning', solid: true })
                    return
                }

                const startRow = 1
                this.registrosProcesados = []
                this.erroresValidacion = []
                this.validacionFallida = false

                const esReclasificacion = this.tipoCarga === 'reclasificaciones'
                const esTopside = this.tipoCarga === 'topside'

                for (let i = startRow; i < jsonData.length; i++) {
                    const row = jsonData[i]
                    if (!row || row.length === 0) continue

                    const tieneAlgo = row.some(
                        (c) => c !== null && c !== undefined && String(c).trim() !== ""
                    )
                    if (!tieneAlgo) continue

                    if (esReclasificacion) {
                        // FORMATO RECLASIFICACIONES: 8 columnas
                        // A: Descripción Ajuste (0) | B: NIT (1) | C: Nombre Compañía (2) - NO SE USA
                        // D: Cuenta (3) | E: Descripción (4) | F: NIIF (5) | G: Valor Local (6) | H: Valor Funcional (7)
                        const descripcionAjuste = row[0] || ''
                        const nit = row[1] || ''
                        const cuenta = row[3] || ''
                        const descripcion = row[4] || ''
                        const niif = row[5] || ''
                        const valorLocalRaw = row[6]
                        const valorFuncionalRaw = row[7]

                        const valorLocal = this.parseValorNumerico(valorLocalRaw)
                        const valorFuncional = this.parseValorNumerico(valorFuncionalRaw)

                        if (cuenta || nit) {
                            this.registrosProcesados.push({
                                descripcionAjuste: String(descripcionAjuste).trim(),
                                nit: String(nit).trim(),
                                cuenta: String(cuenta).trim(),
                                descripcion: String(descripcion).trim(),
                                niif: String(niif).trim(),
                                valorLocal: isNaN(valorLocal) ? 0 : valorLocal,
                                valorFuncional: isNaN(valorFuncional) ? 0 : valorFuncional,
                                _fila: i + 1
                            })
                        }
                    } else if (esTopside) {
                        // FORMATO TOPSIDE: 7 columnas
                        // A: Descripción Ajuste (0) | B: NIT (1) | C: Nombre Compañía (2) - NO SE USA
                        // D: Cuenta (3) | E: Descripción (4) | F: Valor Local (5) | G: Valor Funcional (6)
                        // La columna H (índice 7) NO debe tener datos.
                        const descripcionAjuste = row[0]
                        const identificacion = row[1]
                        const cuenta = row[3]
                        const descripcion = row[4]
                        const valorLocalRaw = row[5]
                        const valorFuncionalRaw = row[6]

                        const colH = row[7]
                        if (colH !== null && colH !== undefined && String(colH).trim() !== "") {
                            this.erroresValidacion.push({
                                fila: i + 1,
                                mensaje: `La columna H no debe tener datos en Topside (encontrado: "${colH}"). El formato correcto solo tiene 7 columnas (A-G).`
                            })
                        }

                        const valorLocal = this.parseValorNumerico(valorLocalRaw)
                        const valorFuncional = this.parseValorNumerico(valorFuncionalRaw)

                        if (this.esVacio(descripcionAjuste)) {
                            this.erroresValidacion.push({ fila: i + 1, mensaje: "Columna A (Descripción Ajuste) vacía." })
                        }

                        if (this.esVacio(identificacion)) {
                            this.erroresValidacion.push({ fila: i + 1, mensaje: "Columna B (Identificación/NIT) vacía." })
                        } else {
                            const nitStr = String(identificacion).trim()
                            if (!/^\d+$/.test(nitStr)) {
                                this.erroresValidacion.push({ fila: i + 1, mensaje: `Columna B (Identificación/NIT) no es numérica: "${nitStr}".` })
                            }
                        }

                        if (this.esVacio(cuenta)) {
                            this.erroresValidacion.push({ fila: i + 1, mensaje: "Columna D (Cuenta) vacía." })
                        } else {
                            const cuentaStr = String(cuenta).trim()
                            if (!/^\d+$/.test(cuentaStr)) {
                                this.erroresValidacion.push({ fila: i + 1, mensaje: `Columna D (Cuenta) no es numérica: "${cuentaStr}".` })
                            }
                        }

                        if (this.esVacio(descripcion)) {
                            this.erroresValidacion.push({ fila: i + 1, mensaje: "Columna E (Descripción) vacía." })
                        }

                        if (this.esVacio(valorLocalRaw)) {
                            this.erroresValidacion.push({ fila: i + 1, mensaje: "Columna F (Valor Local) vacía." })
                        } else if (isNaN(valorLocal)) {
                            this.erroresValidacion.push({ fila: i + 1, mensaje: `Columna F (Valor Local) no es numérica: "${valorLocalRaw}".` })
                        }

                        if (this.esVacio(valorFuncionalRaw)) {
                            this.erroresValidacion.push({ fila: i + 1, mensaje: "Columna G (Valor Funcional) vacía." })
                        } else if (isNaN(valorFuncional)) {
                            this.erroresValidacion.push({ fila: i + 1, mensaje: `Columna G (Valor Funcional) no es numérica: "${valorFuncionalRaw}".` })
                        }

                        if (
                            !this.esVacio(cuenta) &&
                            !this.esVacio(identificacion) &&
                            !isNaN(valorLocal) &&
                            !isNaN(valorFuncional)
                        ) {
                            this.registrosProcesados.push({
                                descripcionAjuste: String(descripcionAjuste).trim(),
                                identificacion: String(identificacion).trim(),
                                cuenta: String(cuenta).trim(),
                                descripcion: String(descripcion || "").trim(),
                                valorLocal: valorLocal,
                                valorFuncional: valorFuncional,
                                _fila: i + 1
                            })
                        }
                    } else {
                        // FORMATO AJUSTES: 10 columnas
                        // A: Secuencia (0) | B: Código (1) | C: Tipo de Ajuste (2) - NO SE USA
                        // D: Descripción Ajuste (3) | E: NIT (4) | F: Nombre Compañía (5) - NO SE USA
                        // G: Cuenta (6) | H: Descripción (7) | I: Valor Local (8) | J: Valor Funcional (9)
                        if (row.length >= 10) {
                            const secuencia = row[0] || ''
                            const codigo = row[1] || ''
                            const descripcionAjuste = row[3] || ''
                            const nit = row[4] || ''
                            const cuenta = row[6] || ''
                            const descripcion = row[7] || ''
                            const valorLocal = this.parseValorNumerico(row[8])
                            const valorFuncional = this.parseValorNumerico(row[9])

                            if (codigo || cuenta) {
                                this.registrosProcesados.push({
                                    secuencia: secuencia.toString(),
                                    codigo: codigo.toString(),
                                    descripcionAjuste: descripcionAjuste.toString(),
                                    nit: nit.toString(),
                                    cuenta: cuenta.toString(),
                                    descripcion: descripcion.toString(),
                                    valorLocal: isNaN(valorLocal) ? 0 : valorLocal,
                                    valorFuncional: isNaN(valorFuncional) ? 0 : valorFuncional,
                                    _fila: i + 1
                                })
                            }
                        }
                    }
                }

                // ============================================================
                // VALIDACIONES ESPECÍFICAS POR TIPO
                // ============================================================
                if (esReclasificacion && this.registrosProcesados.length > 0) {
                    this.validarReclasificaciones()
                }

                if (esTopside) {
                    this.validarTopside()
                }

                this.registrosCount = this.registrosProcesados.length

                if (this.registrosCount === 0 && this.erroresValidacion.length === 0) {
                    this.$bvToast?.toast(
                        `No se encontraron datos válidos en la hoja seleccionada. Verifique que el formato sea correcto para el tipo de carga "${this.tipoCarga}".`,
                        { title: 'Advertencia', variant: 'warning', solid: true, autoHideDelay: 8000 }
                    )
                } else if (this.registrosCount > 0 && this.erroresValidacion.length === 0) {
                    this.$bvToast?.toast(
                        `Se encontraron ${this.registrosCount} registros válidos en la hoja seleccionada.`,
                        { title: 'Información', variant: 'info', solid: true, autoHideDelay: 3000 }
                    )
                }

            } catch (error) {
                console.error("Error procesando datos:", error)
                this.$bvToast?.toast(
                    "Error al procesar los datos del archivo: " + error.message,
                    { title: 'Error', variant: 'danger', solid: true }
                )
            }
        },

        /**
         * Valida que cada cuenta de reclasificación tenga su contrapartida.
         */
        validarReclasificaciones() {
            const errores = []
            const agrupadoPorCuenta = {}

            this.registrosProcesados.forEach((reg, index) => {
                const cuenta = (reg.cuenta || "").trim()

                if (!cuenta) {
                    errores.push({
                        cuenta: "(sin cuenta)",
                        fila: reg._fila || index + 2,
                        mensaje: "Registro sin cuenta",
                        detalle: reg
                    })
                    return
                }

                if (!agrupadoPorCuenta[cuenta]) {
                    agrupadoPorCuenta[cuenta] = []
                }

                agrupadoPorCuenta[cuenta].push({
                    ...reg,
                    _fila: reg._fila || index + 2
                })
            })

            Object.keys(agrupadoPorCuenta).forEach((cuenta) => {
                const registros = agrupadoPorCuenta[cuenta]

                if (registros.length !== 2) {
                    errores.push({
                        cuenta,
                        filas: registros.map(r => r._fila).join(", "),
                        mensaje: `La cuenta aparece ${registros.length} vez/veces. Debe aparecer exactamente 2 veces (una por cada NIIF).`,
                        detalle: registros
                    })
                    return
                }

                const [r1, r2] = registros
                const niif1 = String(r1.niif || "").trim()
                const niif2 = String(r2.niif || "").trim()

                if (niif1 === niif2) {
                    errores.push({
                        cuenta,
                        filas: registros.map(r => r._fila).join(", "),
                        mensaje: `Los dos registros tienen el mismo NIIF (${niif1}). Debe haber uno de origen y otro de destino.`,
                        detalle: registros
                    })
                    return
                }

                const sumaLocal = (r1.valorLocal || 0) + (r2.valorLocal || 0)
                if (Math.abs(sumaLocal) > 0.01) {
                    errores.push({
                        cuenta,
                        filas: registros.map(r => r._fila).join(", "),
                        mensaje: `La suma de Valor Local no es 0 (suma = ${sumaLocal}). NIIF ${niif1}: ${r1.valorLocal} | NIIF ${niif2}: ${r2.valorLocal}.`,
                        detalle: registros
                    })
                    return
                }

                const sumaFuncional = (r1.valorFuncional || 0) + (r2.valorFuncional || 0)
                if (Math.abs(sumaFuncional) > 0.01) {
                    errores.push({
                        cuenta,
                        filas: registros.map(r => r._fila).join(", "),
                        mensaje: `La suma de Valor Funcional no es 0 (suma = ${sumaFuncional}). NIIF ${niif1}: ${r1.valorFuncional} | NIIF ${niif2}: ${r2.valorFuncional}.`,
                        detalle: registros
                    })
                    return
                }
            })

            this.erroresValidacion = errores
            this.validacionFallida = errores.length > 0

            if (this.validacionFallida) {
                this.$bvToast?.toast(
                    `Se encontraron ${errores.length} cuenta(s) sin contrapartida válida. NO se cargará ningún dato.`,
                    { title: "Validación Fallida", variant: "danger", solid: true, autoHideDelay: 6000 }
                )
            } else {
                this.$bvToast?.toast(
                    "Validación de reclasificaciones exitosa.",
                    { title: "Validación OK", variant: "success", solid: true, autoHideDelay: 4000 }
                )
            }
        },

        /**
         * Valida el formato del archivo Topside.
         */
        validarTopside() {
            const errores = [...this.erroresValidacion]

            if (this.registrosProcesados.length === 0 && errores.length === 0) {
                errores.push({
                    fila: "-",
                    mensaje: "No se encontraron registros válidos en el archivo Topside."
                })
            }

            this.registrosProcesados.forEach((reg) => {
                if (
                    this.esVacio(reg.identificacion) ||
                    this.esVacio(reg.cuenta) ||
                    this.esVacio(reg.descripcionAjuste)
                ) {
                    errores.push({
                        fila: reg._fila,
                        mensaje: "Fila con datos incompletos en Topside."
                    })
                }
            })

            this.erroresValidacion = errores
            this.validacionFallida = errores.length > 0

            if (this.validacionFallida) {
                this.$bvToast?.toast(
                    `Se encontraron ${errores.length} error(es) de formato. NO se cargará ningún dato.`,
                    { title: "Validación Fallida", variant: "danger", solid: true, autoHideDelay: 6000 }
                )
            } else {
                this.$bvToast?.toast(
                    "Validación de Topside exitosa.",
                    { title: "Validación OK", variant: "success", solid: true, autoHideDelay: 4000 }
                )
            }
        },

        /**
         * Descarga un Excel con las cuentas/filas que fallaron la validación.
         */
        descargarExcelErroresValidacion() {
            if (this.erroresValidacion.length === 0) {
                this.$toast?.warning("No hay errores de validación para descargar")
                return
            }

            try {
                const data = []
                data.push([
                    "Cuenta", "Fila en Excel", "Mensaje de Error",
                    "NIT", "NIIF", "Valor Local", "Valor Funcional", "Descripción"
                ])

                this.erroresValidacion.forEach((err) => {
                    if (err.detalle && Array.isArray(err.detalle)) {
                        err.detalle.forEach((det) => {
                            data.push([
                                err.cuenta || "",
                                err.filas || det._fila || "",
                                err.mensaje,
                                det.nit || det.identificacion || "",
                                det.niif || "",
                                det.valorLocal || 0,
                                det.valorFuncional || 0,
                                det.descripcion || ""
                            ])
                        })
                    } else {
                        data.push([
                            err.cuenta || "",
                            err.fila || "",
                            err.mensaje,
                            "", "", "", "", ""
                        ])
                    }
                })

                const ws = XLSX.utils.aoa_to_sheet(data)
                const wb = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(wb, ws, "Errores")

                const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" })

                const blob = new Blob([wbout], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                })

                const url = window.URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = url
                link.download = `errores_validacion_${this.tipoCarga}_${new Date().toISOString().slice(0, 10)}.xlsx`

                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)

                this.$toast?.success("Excel de errores descargado correctamente")
            } catch (error) {
                console.error("Error descargando Excel de validación:", error)
                this.$toast?.error("Error al generar el Excel de errores")
            }
        },

        resetCountdown() {
            if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null }
            this.confirmButtonEnabled = false
            this.countdown = 5
        },

        startCountdown() {
            this.resetCountdown()
            this.countdownInterval = setInterval(() => {
                this.countdown -= 1
                if (this.countdown <= 0) {
                    this.confirmButtonEnabled = true
                    clearInterval(this.countdownInterval)
                    this.countdownInterval = null
                }
            }, 1000)
        },

        confirmAndLoad() {
            if (this.isPeriodoDeshabilitado(this.selVigencia)) {
                this.$bvToast?.toast('No se puede proceder con la carga: período deshabilitado', { title: 'Error', variant: 'danger', solid: true })
                return
            }

            if (this.validacionFallida) {
                this.$bvToast?.toast(
                    "No se puede cargar: el archivo tiene errores de formato o datos inválidos. Descargue el Excel de errores para más detalles.",
                    { title: "Validación Fallida", variant: "danger", solid: true, autoHideDelay: 6000 }
                )
                return
            }
            
            if (this.countdownInterval) { 
                clearInterval(this.countdownInterval); 
                this.countdownInterval = null 
            }
            
            if (!this.registrosProcesados || this.registrosProcesados.length === 0) {
                this.$bvToast?.toast('No hay datos para cargar. Por favor seleccione una hoja con datos válidos.', { title: 'Error', variant: 'warning', solid: true })
                return
            }
            
            this.cargaConValidaciones()
        },

        getFileName() { return this.fileName || '' },

        getTipoLabel(tipo) {
            const labels = {
                ajustes: this.labels.ajustes || "Ajustes",
                reclasificaciones: this.labels.reclasificaciones || "Reclasificaciones",
                topside: this.labels.topside || "TopSide"
            }
            return labels[tipo] || tipo
        },

        simulateProgress() {
            this.progressValue = 0
            this.progressMessage = "Validando archivo..."
            this.progressSubMessage = "Verificando estructura y datos"

            const progressInterval = setInterval(() => {
                if (this.progressValue < 90) {
                    this.progressValue += 10
                    if (this.progressValue === 30) this.progressSubMessage = "Validando secuencias en base de datos"
                    else if (this.progressValue === 60) this.progressSubMessage = "Verificando balances por secuencia"
                }
            }, 500)

            return progressInterval
        },

        async cargaConValidaciones() {
            this.cargaFallida = false
            this.cargaParcial = false
            this.mensajeError = ""
            this.erroresDetalle = []
            this.registrosProcesadosCount = 0
            this.registrosConError = 0
            this.totalRegistros = 0

            if (!this.registrosProcesados || this.registrosProcesados.length === 0) {
                this.$bvToast?.toast('No hay datos para cargar.', { title: 'Error', variant: 'danger', solid: true })
                return
            }

            this.isLoadingCarga = true
            this.progressValue = 0
            this.progressMessage = "Validando archivo..."
            this.progressSubMessage = "Verificando estructura y datos"

            const progressInterval = this.simulateProgress()

            const form = {
                vigencia: this.formatVigenciaBackend(this.selVigencia),
                listadoCuentas: this.registrosProcesados,
                idUser: this.$store.state.userId,
                tipoCarga: this.tipoCarga
            }

            try {
                // ============================================================
                // LLAMADA AL ENDPOINT SEGÚN TIPO DE CARGA
                // ============================================================
                let response

                if (this.tipoCarga === "topside") {
                    response = await Cargue.validarYCargarTopside(form)
                } else {
                    response = await Cargue.validarYCargarAjustes(form)
                }

                if (response.data.success) {
                    this.registrosProcesadosCount = response.data.registrosProcesados || 0
                    this.registrosConError = response.data.registrosConError || 0
                    this.totalRegistros = response.data.totalRegistros || this.registrosProcesados.length
                    this.erroresDetalle = response.data.errores || []
                    this.mensajeError = response.data.message || ""

                    if (this.registrosConError > 0 && this.registrosProcesadosCount > 0) {
                        this.cargaParcial = true
                        this.showDownloadButton = true
                        this.progressMessage = "¡Carga parcial completada!"
                        this.progressSubMessage = `Procesados: ${this.registrosProcesadosCount}, Errores: ${this.registrosConError}`
                        this.$toast?.warning(this.mensajeError)
                        this.limpiarFormularioManteniendoDescarga()
                    } else if (this.registrosConError === 0 && this.registrosProcesadosCount > 0) {
                        this.progressMessage = "¡Carga completada exitosamente!"
                        this.progressSubMessage = `Se procesaron ${this.registrosProcesadosCount} registros correctamente`
                        this.$toast?.success(this.mensajeError || "Carga completada exitosamente")
                        this.progressValue = 100
                        this.showDownloadButton = false
                        setTimeout(() => {
                            this.isLoadingCarga = false
                            this.showSuccessMessage = true
                            if (this.successTimer) clearTimeout(this.successTimer)
                            this.successTimer = setTimeout(() => {
                                this.showSuccessMessage = false
                                this.resetForm()
                                this.successTimer = null
                            }, 3000)
                        }, 2000)
                    } else {
                        this.cargaFallida = true
                        this.showDownloadButton = true
                        this.progressMessage = "Carga fallida"
                        this.progressSubMessage = "Todos los registros presentaron errores"
                        this.$toast?.error(this.mensajeError || "No se pudo cargar ningún registro")
                        this.limpiarFormularioManteniendoDescarga()
                    }

                    this.progressValue = 100
                } else {
                    this.cargaFallida = true
                    this.showDownloadButton = true
                    this.mensajeError = response.data.message || "Error al procesar la carga"
                    this.erroresDetalle = response.data.errores || []
                    this.totalRegistros = this.registrosProcesados.length || 0
                    this.registrosConError = this.totalRegistros
                    this.registrosProcesadosCount = response.data.registrosProcesados || 0
                    this.progressMessage = "Carga fallida"
                    this.progressSubMessage = "Error al procesar el archivo"
                    this.progressValue = 100
                    this.$toast?.error(this.mensajeError)
                    this.limpiarFormularioManteniendoDescarga()
                }
            } catch (error) {
                console.error("Error en carga:", error)
                this.cargaFallida = true
                this.showDownloadButton = true
                
                if (error.response) {
                    this.mensajeError = error.response.data?.message || error.response.data?.error || "Error al procesar la carga"
                    this.erroresDetalle = error.response.data?.errores || []
                } else {
                    this.mensajeError = "Error al conectar con el servidor"
                    this.erroresDetalle = []
                }
                
                if (!this.erroresDetalle || this.erroresDetalle.length === 0) {
                    this.erroresDetalle = [{
                        mensaje: this.mensajeError,
                        tipo: 'error_general',
                        detalle: error.message || 'Error desconocido'
                    }]
                }
                
                this.totalRegistros = this.registrosProcesados.length || 0
                this.registrosConError = this.totalRegistros
                this.registrosProcesadosCount = 0
                this.progressMessage = "Error en la carga"
                this.progressSubMessage = this.mensajeError
                this.progressValue = 100
                this.$toast?.error(this.mensajeError)
                this.limpiarFormularioManteniendoDescarga()
            } finally {
                clearInterval(progressInterval)
                this.isLoadingCarga = false
            }
        },

        limpiarFormularioManteniendoDescarga() {
            // Guardar los errores y el estado de descarga
            const erroresGuardados = [...this.erroresDetalle]
            const showDownloadButtonGuardado = true
            
            // Limpiar TODO el formulario
            this.selVigencia = ""
            this.tipoCarga = ""
            this.listHojas = []
            this.showBtnHoja = false
            this.selectedHoja = ""
            this.registrosProcesados = []
            this.registrosCount = 0
            this.fileName = ""
            this.workbook = null
            this.fileReader = null
            this.isUploading = false
            this.showSuccessMessage = false
            this.cargaFallida = false
            this.cargaParcial = false
            this.progressValue = 0
            this.progressMessage = "Iniciando carga..."
            this.progressSubMessage = "Procesando archivo Excel"
            this.confirmButtonEnabled = false
            this.countdown = 5
            this.erroresValidacion = []
            this.validacionFallida = false
            if (this.countdownInterval) { 
                clearInterval(this.countdownInterval); 
                this.countdownInterval = null 
            }
            if (this.$refs.fileInput) this.$refs.fileInput.value = ''
            
            // Restaurar SOLO los errores y el botón de descarga
            this.erroresDetalle = erroresGuardados
            this.showDownloadButton = showDownloadButtonGuardado
        },

        async descargarExcelErrores() {
            if (!this.erroresDetalle || this.erroresDetalle.length === 0) {
                this.$toast?.warning("No hay errores para descargar")
                return
            }

            try {
                const response = await Cargue.generarExcelErrores({ errores: this.erroresDetalle })
                
                if (!response || !response.data) {
                    throw new Error('No se recibieron datos del servidor')
                }

                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                })
                
                if (blob.size === 0) {
                    throw new Error('El archivo generado está vacío')
                }

                const url = window.URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = url
                const fecha = new Date().toISOString().slice(0, 10)
                link.download = `novedades_carga_${fecha}.xlsx`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                
                setTimeout(() => {
                    window.URL.revokeObjectURL(url)
                }, 100)

                this.$toast?.success("Excel de novedades descargado correctamente")
            } catch (error) {
                console.error("Error descargando Excel:", error)
                this.descargarErroresComoTexto()
            }
        },

        descargarErroresComoTexto() {
            try {
                let texto = "NOVEDADES DE CARGA\n"
                texto += `Fecha: ${new Date().toLocaleString()}\n`
                texto += `${'='.repeat(60)}\n\n`

                if (!this.erroresDetalle || this.erroresDetalle.length === 0) {
                    texto += "No hay errores detallados disponibles.\n"
                } else {
                    this.erroresDetalle.forEach((error, index) => {
                        if (typeof error === 'object' && error !== null) {
                            texto += `Error ${index + 1}:\n`
                            texto += `  Mensaje: ${error.mensaje || error.message || 'Error desconocido'}\n`
                            texto += `  Tipo: ${error.tipo || 'error'}\n`
                            if (error.detalle || error.detalles) {
                                texto += `  Detalle: ${error.detalle || error.detalles}\n`
                            }
                            texto += '\n'
                        } else {
                            texto += `Error ${index + 1}: ${String(error)}\n\n`
                        }
                    })
                }

                const blob = new Blob([texto], { type: 'text/plain;charset=utf-8' })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = url
                const fecha = new Date().toISOString().slice(0, 10)
                link.download = `novedades_carga_${fecha}.txt`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                setTimeout(() => {
                    window.URL.revokeObjectURL(url)
                }, 100)

                this.$toast?.success("Archivo de novedades descargado correctamente")
            } catch (e) {
                console.error("Error descargando como texto:", e)
                this.$toast?.error("No se pudo generar el archivo de novedades")
            }
        },

        resetForm(keepVigencia = false) {
            if (this.successTimer) { clearTimeout(this.successTimer); this.successTimer = null }
            if (!keepVigencia) this.selVigencia = ""
            this.tipoCarga = ""
            this.listHojas = []
            this.showBtnHoja = false
            this.selectedHoja = ""
            this.fileName = ""
            this.workbook = null
            this.fileReader = null
            this.isUploading = false
            this.isLoadingCarga = false
            this.showSuccessMessage = false
            this.registrosProcesados = []
            this.registrosCount = 0
            this.cargaFallida = false
            this.cargaParcial = false
            this.mensajeError = ""
            this.erroresDetalle = []
            this.registrosProcesadosCount = 0
            this.registrosConError = 0
            this.totalRegistros = 0
            this.showDownloadButton = false
            this.progressValue = 0
            this.progressMessage = "Iniciando carga..."
            this.progressSubMessage = "Procesando archivo Excel"
            this.erroresValidacion = []
            this.validacionFallida = false
            if (this.$refs.fileInput) this.$refs.fileInput.value = ''
            this.resetCountdown()
        }
    },

    mounted() {
        this.isLoading = false
    },

    beforeDestroy() {
        if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null }
        if (this.successTimer) { clearTimeout(this.successTimer); this.successTimer = null }
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
/* ===== SPINNER MODERNO ===== */
.modern-spinner {
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto;
}

.spinner-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 4px solid transparent;
    animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
    border-top-color: #1f4f83;
    animation-delay: -0.45s;
}

.spinner-ring:nth-child(2) {
    border-right-color: #4a90d9;
    animation-delay: -0.3s;
}

.spinner-ring:nth-child(3) {
    border-bottom-color: #7fb3e6;
    animation-delay: -0.15s;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.modern-progress {
    background: #e9ecef;
    overflow: hidden;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.modern-progress .progress-bar {
    background: linear-gradient(90deg, #1f4f83, #4a90d9);
    transition: width 0.3s ease;
    font-weight: 600;
    font-size: 0.75rem;
    line-height: 8px;
    color: transparent;
    text-shadow: none;
}

/* ===== FIN SPINNER MODERNO ===== */

.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
    flex-wrap: wrap;
}

.filter-date-wrapper {
    display: inline-flex;
    align-items: center;
}

.filter-date-input {
    min-width: 180px;
    max-width: 260px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
    border: 1px solid #ced4da;
    padding: 0 12px;
    background-color: #fff;
    color: #495057;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filter-date-input:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.filter-date-input.disabled-period {
    border-color: #dc3545;
    background-color: #f8d7da;
}

.filter-date-input:disabled {
    background-color: #e9ecef;
    opacity: 1;
    cursor: not-allowed;
}

.niif-header .filter-select {
    min-width: 180px;
    max-width: 260px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #e9ecef;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.admin-table-block {
    margin-top: 1.5rem !important;
}

.table-corporate-shell {
    background: #ffffff;
    border: 1px solid #e4e8ed;
    border-radius: 10px;
    padding: 2rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    position: relative;
}

.upload-spinner-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 0;
    width: 100%;
    min-height: 200px;
}

.upload-content-wrapper {
    width: 100%;
    max-width: 100%;
}

.hidden-file-input {
    display: none;
}

.upload-drop-zone {
    width: 100%;
    border: 2px dashed #d1d5db;
    border-radius: 12px;
    padding: 3rem 2rem;
    cursor: pointer;
    background-color: #fafbfc;
    position: relative;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.upload-drop-zone.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    border-color: #d1d5db;
    background-color: #f8f9fa;
}

.upload-drop-zone.has-file {
    border-color: #2e507a;
    background-color: #e8edf5;
    padding: 1.5rem 2rem;
    min-height: 120px;
}

.upload-drop-zone.drag-over {
    border-color: #2e507a;
    background-color: #dce3ef;
    transform: scale(1.01);
}

.upload-content {
    width: 100%;
    text-align: center;
}

.upload-icon {
    font-size: 3.5rem;
    color: #6c757d;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
}

.upload-icon.has-file {
    font-size: 2.5rem;
    color: #2e507a;
    margin-bottom: 0.5rem;
}

.upload-title {
    color: #495057;
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    word-break: break-all;
}

.upload-title.text-muted {
    color: #6c757d !important;
}

.upload-subtitle {
    color: #6c757d;
    margin-bottom: 0;
    font-size: 0.9rem;
}

.upload-subtitle.text-muted {
    color: #adb5bd !important;
}

.upload-file-info-wrapper {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    background: #ffffff;
    border-radius: 6px;
    font-weight: 500;
    margin-top: 0.5rem;
    font-size: 0.95rem;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.remove-file-btn {
    padding: 0 0.3rem;
    color: #dc3545;
    font-size: 1.1rem;
    cursor: pointer;
    line-height: 1;
}

.sheet-selector-container {
    max-width: 100%;
    margin: 1.5rem auto 0;
    width: 100%;
}

.sheet-selector-container .filter-select {
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
    flex: 1;
    min-width: 150px;
    max-width: 400px;
}

.download-btn {
    height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #28a745 !important;
    color: white !important;
    border: 1px solid #28a745 !important;
    cursor: pointer;
    flex-shrink: 0;
}

.download-btn:hover:not(:disabled) {
    background-color: #218838 !important;
    border-color: #1e7e34 !important;
}

.download-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.novedades-label {
    font-size: 14px;
    font-weight: 500;
    color: #1f4f83;
    background: #e8edf5;
    padding: 6px 14px;
    border-radius: 7px;
    border: 1px solid #d1dceb;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
}

.upload-action-panel {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: 1.5rem;
    background: #f8fafc;
    border-radius: 10px;
    border: 1px solid #e4e8ed;
}

.confirmation-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem 2rem;
}

.info-item {
    display: flex;
    justify-content: space-between;
    padding: 0.4rem 0;
    border-bottom: 1px solid #e9ecef;
}

.info-label {
    font-weight: 600;
    color: #495057;
}

.info-value {
    color: #2e507a;
    font-weight: 500;
}

.info-value.text-muted {
    color: #6c757d !important;
    font-weight: 400;
}

.upload-btn {
    min-width: 200px;
    padding: 0.6rem 2rem;
    font-size: 1rem;
    border-radius: 7px;
    background-color: #1f4f83 !important;
    border-color: #1f4f83 !important;
    color: white !important;
}

.upload-btn:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.upload-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    border-color: #6c757d !important;
}

.success-message {
    text-align: center;
    padding: 1.5rem 0;
    animation: fadeInUp 0.5s ease;
}

.success-icon {
    font-size: 4rem;
    color: #28a745;
    margin-bottom: 1rem;
    display: block;
}

.success-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #28a745;
    margin-bottom: 0.5rem;
}

.success-subtitle {
    font-size: 1rem;
    color: #6c757d;
}

@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 992px) {
    .confirmation-info-grid { grid-template-columns: 1fr; gap: 0.5rem; }
    .sheet-selector-container { max-width: 100%; }
}

@media (max-width: 768px) {
    .upload-drop-zone { padding: 1.5rem 1rem; min-height: 140px; }
    .upload-title { font-size: 0.95rem; }
    .upload-btn { min-width: 100%; width: 100%; }
    .table-corporate-shell { padding: 1rem; min-height: 250px; }
    .upload-icon { font-size: 2.5rem; }
    .upload-drop-zone.has-file { padding: 1rem; min-height: 100px; }
    .upload-action-panel { padding: 1rem; }
    .success-icon { font-size: 3rem; }
    .success-title { font-size: 1.1rem; }
    .modern-spinner { width: 50px; height: 50px; }
    .sheet-selector-container .d-flex { flex-wrap: wrap !important; }
    .sheet-selector-container .filter-select { min-width: 100px; max-width: 100%; flex: 1 1 200px; }
    .download-btn { flex: 0 1 auto; }
    .novedades-label { flex: 0 1 auto; white-space: normal; font-size: 12px; padding: 4px 10px; }
}

@media (max-width: 576px) {
    .param-title { font-size: 1.5rem !important; }
    .upload-drop-zone { padding: 1rem 0.75rem; min-height: 120px; }
    .upload-icon { font-size: 2rem; }
    .table-corporate-shell { padding: 0.75rem; min-height: 200px; }
    .upload-file-info-wrapper { font-size: 0.8rem; padding: 0.3rem 0.6rem; }
    .upload-drop-zone.has-file { min-height: 80px; padding: 0.75rem; }
    .success-icon { font-size: 2.5rem; }
    .success-title { font-size: 1rem; }
    .modern-spinner { width: 40px; height: 40px; }
    .modern-spinner .spinner-ring { border-width: 3px; }
}
</style>